import {useCallback, useEffect, useState} from "react";

const LOCATION_CACHE_KEY = 'CACHED_LOCATION';
const CACHE_EXPIRATION_MINUTES = 15;

export default function useLocation(defaultLocation = null) {
    const [userLocation, setUserLocation] = useState(defaultLocation);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const getLocationFromCache = useCallback(() => {
        const cachedLocationStr = localStorage.getItem(LOCATION_CACHE_KEY);
        if (!cachedLocationStr) {
            return null;
        }
        const { value, expirationTime } = JSON.parse(cachedLocationStr);
        const now = new Date().getTime();
        if (now >= expirationTime) {
            localStorage.removeItem(LOCATION_CACHE_KEY);
            return null;
        }
        return value;
    }, []);

    const cacheLocationInStorage = useCallback((location) => {
        const expirationTime = new Date().getTime() + CACHE_EXPIRATION_MINUTES * 60 * 1000;
        const cacheValue = JSON.stringify({ value: location, expirationTime });
        localStorage.setItem(LOCATION_CACHE_KEY, cacheValue);
    }, []);

    const getCurrentLocation = useCallback(() => {
        return new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    resolve({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    });
                },
                (error) => {
                    reject(error);
                },
                {
                    timeout: 5000,
                }
            );
        });
    }, []);

    const fetchLocation = useCallback(async () => {
        try {
            setIsLoading(true);
            const cachedLocation = getLocationFromCache();
            if (cachedLocation) {
                setUserLocation(cachedLocation);
                setIsLoading(false);
                return;
            }

            const currentLocation = await getCurrentLocation();
            cacheLocationInStorage(currentLocation);
            setUserLocation(currentLocation);
            setIsLoading(false);
        } catch (error) {
            setError(error);
            setIsLoading(false);
        }
    }, [getLocationFromCache, getCurrentLocation, cacheLocationInStorage]);

    useEffect(() => {
        const abortController = new AbortController();

        fetchLocation();

        return () => {
            abortController.abort();
        };
    }, [fetchLocation]);

    return { userLocation, isLoading, error };
}